import type { MessagePayload, Unsubscribe } from "firebase/messaging";
import { createGlobalState, tryOnScopeDispose, useThrottleFn } from "@vueuse/core";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { customToast } from "../components/Sonner/SonnerCustom";
import { useAuthStore } from "./AuthStore/useAuthStore";
import { useLocationStore } from "./LocationStore/useLocationStore";
import { createPushMessageHandler } from "./Notifications/pushMessageHandler";
import { useUserNotificationsLive } from "./Notifications/v2/useUserNotificationsLive";
import { useDeviceState } from "./useDeviceState";
import { useMarkNotificationAsRead } from "./useMarkNotificationAsRead";
import { useNavbarUnreadCount } from "./useNavbarUnreadCount";
import { useRecentlyCreatedEntities } from "./useRecentlyCreatedEntities";

const VAPID_KEY = "BNP0kOXWCJWv3gnQvHZ10GzqtwVdedjSa0kKeOP1IjDmeQZZ_6k2zrm__ONfqUACzeL0mHJOdnleqLu2HZeAlU4";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyCOWQ4W4e9K544Itbts_gsTpGc-AEIgVsg",
  authDomain: "verbleif-489a4.firebaseapp.com",
  databaseURL: "https://verbleif-489a4.firebaseio.com",
  projectId: "verbleif-489a4",
  storageBucket: "verbleif-489a4.firebasestorage.app",
  messagingSenderId: "572754758664",
  appId: "1:572754758664:web:d0a4899006b1f49aa5d5bd",
  measurementId: "G-MBT9BYGQJQ",
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
// Resolves to null instead of rejecting so that a browser without messaging support (or a
// failed init) degrades to "no messaging" rather than surfacing as an unhandled rejection at
// module load, where no caller is attached yet.
export const messaging = isSupported().then((supported) => {
  if (!supported) {
    return null;
  }
  return getMessaging(app);
}).catch((error) => {
  console.log("[useFirebase] Messaging unavailable", error);
  return null;
});

export async function deleteFirebaseToken() {
  const messagingInstance = await messaging;
  if (!messagingInstance) {
    return;
  }

  await deleteToken(messagingInstance);
}

export const useFirebase = createGlobalState(() => {
  const granted = ref(
    window.Notification && window.Notification.permission === "granted",
  );
  const { checkUnreadCount } = useNavbarUnreadCount();
  const { isLive, resync } = useUserNotificationsLive();
  const { user, isAuthenticated, device } = useAuthStore();
  const { modeSelectedLocation } = useLocationStore();
  const { hasEntity } = useRecentlyCreatedEntities();
  const { markNotificationAsReadById, markNotificationsAsReadByEntity } = useMarkNotificationAsRead();
  const lastToken = ref<string | null>(null);
  const unsubscribe = ref<Unsubscribe | null>(null);
  const isTryingToSubscribe = ref(false);
  const { updateDevice } = useDeviceState();
  const router = useRouter();
  const { t } = useI18n();

  const throttleUpdateDevice = useThrottleFn(updateDevice, 10000);

  const handlePushMessage = createPushMessageHandler({
    currentUserIri: () => user.value?.["@id"] ?? null,
    selectedLocation: () => modeSelectedLocation.value,
    isLive: () => isLive.value,
    checkUnreadCount,
    resync,
    hasEntity,
    markNotificationAsReadById,
    markNotificationsAsReadByEntity,
    navigate: location => router.push(location),
    translate: key => t(key),
    showToast: (componentProps) => {
      setTimeout(() => {
        customToast({ id: Date.now(), duration: 6000, componentProps });
      }, 1);
    },
  });

  function handleMessage(payload: MessagePayload) {
    try {
      console.log("[useFirebase] Received message", payload);
      handlePushMessage(payload);
    } catch (error) {
      console.error("[useFirebase] Error handling Firebase message:", error);
      // Don't throw here to prevent breaking the message listener
    }
  }

  async function subscribeToMessages() {
    if (isTryingToSubscribe.value) {
      return;
    }
    isTryingToSubscribe.value = true;
    try {
      if (!granted.value) {
        return;
      }

      const token = await getToken(getMessaging(), { vapidKey: VAPID_KEY });

      if (!lastToken.value || token !== lastToken.value) {
        await updateDevice(token);

        lastToken.value = token;
      }

      console.log("[useFirebase] Device", device.value);

      console.log("[useFirebase] Setting up Firebase message listener");

      // Clean up any existing subscription
      unsubscribeFromMessages();

      unsubscribe.value = onMessage(getMessaging(), handleMessage);
      console.log("[useFirebase] Firebase message listener set up");
    } catch (error) {
      console.error("[useFirebase] Error setting up Firebase message listener:", error);
    } finally {
      isTryingToSubscribe.value = false;
    }
  }

  function unsubscribeFromMessages() {
    if (unsubscribe.value) {
      unsubscribe.value();
      unsubscribe.value = null;
    }
  }
  const route = useRoute();

  const skipRoutes = computed(() => {
    return route.meta.public || route.meta.skipAuthChecks || route.name === undefined;
  });

  // Watch for permission changes and handle cleanup
  watch([() => granted.value, () => isAuthenticated.value, () => skipRoutes.value], ([isGranted, isAuthenticated, shouldSkip]) => {
    if (shouldSkip) {
      return;
    }

    if (isAuthenticated) {
      if (isGranted) {
        subscribeToMessages();
      } else {
        console.log("[useFirebase] Permission denied, updating device");
        throttleUpdateDevice();
      }
    } else {
      unsubscribeFromMessages();
    }
  }, { immediate: true });

  // Cleanup on component/scope disposal
  tryOnScopeDispose(() => {
    unsubscribeFromMessages();
  });

  async function initialize(): Promise<boolean> {
    if (await shouldRequestNotificationPermission()) {
      const permission = await Notification.requestPermission();
      granted.value = permission === "granted";
    }

    return granted.value;
  }

  async function shouldRequestNotificationPermission() {
    if (!window.Notification) {
      return false;
    }
    if (!(await isSupported())) {
      return false;
    }
    return window.Notification.permission === "default";
  }

  return {
    initialize,
    isSupported,
    granted,
    unsubscribeFromMessages,
  };
});
